/**
 * Authentication API Client
 *
 * This module provides client functions for authentication with the backend
 * JWT authentication system using MetaMask signature verification.
 */

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "https://backend-api.backend.usdx.live";
const AUTH_API_BASE_URL = `${BACKEND_URL}/api/admin/auth`;

/**
 * Login request parameters
 */
export interface LoginParams {
  address: string;
  signature: string;
}

/**
 * Login response
 */
export interface LoginResponse {
  status: true;
  message: string;
  data: {
    token: string;
    user: {
      address: string;
      hasWallet: boolean;
    };
  };
}

/**
 * Token verification response
 */
export interface VerifyResponse {
  status: true;
  message: string;
  data: {
    user: {
      address: string;
    };
  };
}

/**
 * Error response
 */
export interface AuthErrorResponse {
  status: false;
  message: string;
}

/**
 * Fetch a server-issued, single-use signing message for the given address.
 * The nonce is valid for 5 minutes. Always call this immediately before signing.
 */
export async function fetchLoginMessage(address: string): Promise<string> {
  const res = await fetch(`${AUTH_API_BASE_URL}/nonce?address=${encodeURIComponent(address.toLowerCase())}`);
  if (!res.ok) throw new Error("Failed to fetch login nonce");
  const data = await res.json();
  if (!data.status || !data.data?.message) throw new Error("Invalid nonce response");
  return data.data.message as string;
}

/**
 * Authenticated request utility
 * Automatically includes JWT token from localStorage
 */
/**
 * Cookie utilities for auth
 */
const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Authenticated request utility
 * Automatically includes JWT token from cookies and handles token refresh
 */
export const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<Response | undefined> => {
  try {
    const token = getCookie("authToken");
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 403 && retryCount === 0) {
      const data = await response.json();
      if (data.message === "Invalid or expired token") {
        console.log("AuthAPI: Token expired, attempting automatic re-authentication...");

        // Check if we have a wallet available for re-authentication
        if (window.ethereum && retryCount === 0) {
          try {
            console.log("AuthAPI: Attempting to get wallet address for re-authentication");

            // Get the current wallet address
            const accounts = await window.ethereum.request({ method: "eth_accounts" });
            const walletAddress = accounts[0];

            if (walletAddress) {
              console.log("AuthAPI: Re-authenticating with wallet:", walletAddress);

              // Fetch server-issued nonce message, then sign it
              const message = await fetchLoginMessage(walletAddress);

              const signature = await window.ethereum.request({
                method: "personal_sign",
                params: [message, walletAddress],
              });

              // Make login request — do NOT send the message; server uses its stored nonce
              const loginResponse = await fetch(`${AUTH_API_BASE_URL}/login`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  address: walletAddress,
                  signature,
                }),
              });

              if (loginResponse.ok) {
                const loginData = await loginResponse.json();
                if (loginData.status && loginData.data) {
                  const newToken = loginData.data.token;

                  // Set new cookie
                  const setCookie = (name: string, value: string, days = 7) => {
                    const expires = new Date();
                    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
                    const isProduction = window.location.protocol === "https:";
                    const sameSite = isProduction ? "Strict" : "Lax";
                    const secure = isProduction ? ";Secure" : "";
                    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=${sameSite}${secure}`;
                  };

                  setCookie("authToken", newToken);
                  console.log("AuthAPI: Re-authentication successful, retrying request...");

                  // Retry the original request with new token
                  const retryResponse = await fetch(url, {
                    ...options,
                    headers: {
                      ...options.headers,
                      Authorization: `Bearer ${newToken}`,
                    },
                  });

                  return retryResponse;
                }
              }
            }
          } catch (reAuthError) {
            console.error("AuthAPI: Re-authentication failed:", reAuthError);
          }
        }

        // If re-authentication failed or no wallet available, clear auth state
        console.log("AuthAPI: Re-authentication failed, clearing auth state");
        redirectToLogin();
        return;
      }
    }

    return response;
  } catch (error) {
    console.error("Request failed:", error);
  }
};

/**
 * Cookie utilities for clearing auth
 */
const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

/**
 * Redirect to login (trigger wallet connection)
 */
const redirectToLogin = () => {
  console.log("Token expired, clearing auth state");
  deleteCookie("authToken");
  // In a real app, this might redirect to login page or trigger wallet connection
  // For now, we'll just clear the auth state
};

/**
 * Check authentication status
 */
export const checkAuthStatus = async (): Promise<{ user: any; isAuthenticated: boolean } | null> => {
  try {
    const token = getCookie("authToken");
    if (!token) {
      redirectToLogin();
      return null;
    }

    const response = await fetch(`${AUTH_API_BASE_URL}/verify`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return {
        user: data.data.user,
        isAuthenticated: true,
      };
    } else if (response.status === 403) {
      // Token expired or invalid
      deleteCookie("authToken");
      redirectToLogin();
      return null;
    }
  } catch (error) {
    console.error("Auth check failed:", error);
    redirectToLogin();
  }
  return null;
};
